import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { KpiCard } from "../components/KpiCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

export default function Home() {
  const navigate = useNavigate();
  const [campagnes, setCampagnes] = useState([]);
  const [sites, setSites] = useState([]);
  const [filters, setFilters] = useState({ campagne_id: "", categorie: "", site_id: "", statut: "" });
  const [kpis, setKpis] = useState(null);
  const [activeAudit, setActiveAudit] = useState(null);

  useEffect(() => {
    api.get("/campagnes").then((res) => {
      setCampagnes(res.data);
      const cross = res.data.find((c) => c.type_campagne === "CROSS_ASSESSMENT");
      if (cross) setFilters((f) => ({ ...f, campagne_id: cross.campagne_id }));
    });
    api.get("/sites").then((res) => setSites(res.data));
  }, []);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/dashboard/home", { params }).then((res) => setKpis(res.data));
    if (filters.campagne_id) {
      api.get("/audits", { params: { campagne_id: filters.campagne_id, statut: "EN_COURS" } }).then((res) => {
        setActiveAudit(res.data[0] || null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const categories = [...new Set(sites.map((s) => s.categorie))];

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto" data-testid="home-dashboard-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight">Tableau de Bord</h1>
        <p className="text-sm text-muted-foreground mt-1">Direction Santé &amp; Sécurité — Lafarge Ciments France</p>
      </div>

      {activeAudit && (
        <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" data-testid="active-audit-banner">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-mono uppercase text-emerald-700 dark:text-emerald-400">Audit en cours</div>
              <div className="font-semibold">{activeAudit.site_nom} — {activeAudit.progression}%</div>
            </div>
            <Button data-testid="active-audit-resume-button" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/audits/${activeAudit.audit_id}`)}>
              Reprendre l'audit
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Select value={filters.campagne_id} onValueChange={(v) => setFilters((f) => ({ ...f, campagne_id: v }))}>
          <SelectTrigger data-testid="filter-campagne-select"><SelectValue placeholder="Campagne" /></SelectTrigger>
          <SelectContent>
            {campagnes.map((c) => <SelectItem key={c.campagne_id} value={c.campagne_id}>{c.nom}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.categorie || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, categorie: v === "ALL" ? "" : v }))}>
          <SelectTrigger data-testid="filter-categorie-select"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Toutes catégories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.site_id || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, site_id: v === "ALL" ? "" : v }))}>
          <SelectTrigger data-testid="filter-site-select"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tous les sites</SelectItem>
            {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.nom_site}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.statut || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, statut: v === "ALL" ? "" : v }))}>
          <SelectTrigger data-testid="filter-statut-select"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tous statuts</SelectItem>
            {["NON_DEMARRE", "EN_COURS", "A_REVOIR", "TERMINE", "VALIDE"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="home-kpi-grid">
          <KpiCard title="Audits prévus" value={kpis.audits_prevus} sub={`${kpis.valide} validés`} color="slate" testId="kpi-audits-prevus" />
          <KpiCard title="Non démarrés" value={kpis.non_demarre} color="slate" testId="kpi-non-demarre" />
          <KpiCard title="En cours" value={kpis.en_cours} color="amber" testId="kpi-en-cours" />
          <KpiCard title="Terminés" value={kpis.termine} color="emerald" testId="kpi-termine" />
          <KpiCard title="Progression globale" value={`${kpis.progression_globale}%`} color="emerald" testId="kpi-progression" />
          <KpiCard title="Minimum Requirements" value={kpis.score_minimum_moyen} sub="Score moyen /4" color="sky" testId="kpi-score-minimum" />
          <KpiCard title="World Class" value={kpis.score_world_class_moyen} sub="Score moyen /4" color="sky" testId="kpi-score-world-class" />
          <KpiCard title="Actions ouvertes" value={kpis.actions_ouvertes} sub={`${kpis.actions_critiques} critiques`} color="amber" testId="kpi-actions-ouvertes" />
          <KpiCard title="Actions en retard" value={kpis.actions_en_retard} color="rose" testId="kpi-actions-retard" />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button data-testid="quick-action-sites" onClick={() => navigate("/sites")} className="bg-emerald-600 hover:bg-emerald-700">Démarrer un audit terrain</Button>
        <Button data-testid="quick-action-heatmap" variant="outline" onClick={() => navigate("/heatmap")}>Cartographie des sites (Heatmap)</Button>
        <Button data-testid="quick-action-historique" variant="outline" onClick={() => navigate("/historique")}>Historique 2024 → 2026</Button>
        <Button data-testid="quick-action-rapports" variant="outline" onClick={() => navigate("/rapports")}>Télécharger un rapport PDF</Button>
      </div>
    </div>
  );
}
